
import PhotoPicker from '../../../components/PhotoPicker';
import PhotoSlot from './PhotoSlot';
import { inputStyle, labelStyle, stepDescStyle } from '../styles';
import { OPTIONAL_PHOTOS, STANDARD_PHOTOS } from '../wizardRules';

/**
 * The photographs. Every one is optional (2026-10-06).
 *
 * The four standard views are what an expert looks at, and none can be taken
 * later, so they come first. But a bracket or crust fungus has no stem or
 * gills and a damaged specimen may have lost them, and requiring the views
 * made people upload stand-in photos that misdescribe the specimen. The
 * conditional ones are offered for the same reason they were once demanded:
 * a cut face for a colour change that was reported, a photograph of a plant
 * nobody could name.
 */
export default function Step5({ t, form, vegetation, photos, setPhoto, existingPhotoTypes, extraPhotos,
  setExtraPhotos, otherPhotoNote, setOtherPhotoNote, vegUnknownPhoto,
  setVegUnknownPhoto, setColourChange }) {
  const needsVegUnknown = vegetation.some(v => v.species === 'unknown');
  const optionalToShow = form.colour_change === true
    ? OPTIONAL_PHOTOS.filter(p => p.type !== 'cross_section')
    : OPTIONAL_PHOTOS;

  return (
    <div>
      <p style={stepDescStyle}>{t('step5.desc')}</p>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-fg)', marginBottom: 6 }}>{t('step5.standardViews')}</div>
        {STANDARD_PHOTOS.map(({ type, labelKey, hintKey }) => (
          <PhotoSlot key={type} label={t(labelKey)} hint={t(hintKey)} required={false}
            file={photos[type] || null} alreadyOnFile={existingPhotoTypes.has(type)}
            alreadyOnFileLabel={t('step5.alreadyUploaded')} onChange={f => setPhoto(type, f)} />
        ))}
      </div>

      <div style={{ background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>{t('step5.colourChangeQuestion')}</div>
        <div style={{ display: 'flex', gap: 16, marginBottom: form.colour_change === true ? 12 : 0 }}>
          {[{ val: true, label: t('common:yes') }, { val: false, label: t('common:no') }].map(opt => (
            <label key={String(opt.val)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="radio" name="colour_change" checked={form.colour_change === opt.val} onChange={() => setColourChange(opt.val)} title={t('tip.colourChange')} />
              {opt.label}
            </label>
          ))}
        </div>
        {form.colour_change === true && (
          <>
            <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '2px 0 6px' }}
              title={t('step5.crossSectionHint')}>
              {t('step5.crossSectionHint')}
            </p>
            <PhotoSlot label={t('step5.photo5')} required={false}
              file={photos.cross_section || null} alreadyOnFile={existingPhotoTypes.has('cross_section')}
              alreadyOnFileLabel={t('step5.alreadyUploaded')} onChange={f => setPhoto('cross_section', f)} />
          </>
        )}
      </div>

      {needsVegUnknown && (
        <div data-testid="veg-unknown-photo" style={{ background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--c-text-body-fg)', marginBottom: 8 }}>
            {t('step5.vegPhotoSuggested')}
          </div>
          <PhotoSlot label={t('step5.vegPhotoLabel')} required={false} file={vegUnknownPhoto}
            alreadyOnFile={existingPhotoTypes.has('other')} alreadyOnFileLabel={t('step5.alreadyUploaded')}
            onChange={setVegUnknownPhoto} />
        </div>
      )}

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-fg)', marginBottom: 6 }}>{t('step5.optional')}</div>
        {optionalToShow.map(({ type, labelKey }) => (
          <div key={type}>
            <PhotoSlot label={t(labelKey)} required={false}
              file={photos[type] || null} onChange={f => setPhoto(type, f)} />
            {type === 'other' && photos.other && (
              <label style={{ ...labelStyle, margin: '2px 0 10px' }}>
                {t('step5.describeOther')}
                <input
                  value={otherPhotoNote}
                  onChange={e => setOtherPhotoNote(e.target.value)}
                  placeholder={t('step5.describeOtherPlaceholder')}
                  title={t('tip.describeOther')}
                  style={inputStyle}
                />
              </label>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 16 }} data-testid="wizard-extra-photos">
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text-body-fg)', marginBottom: 2 }}>{t('step5.extraPhotosTitle')}</div>
        <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: 0 }}>{t('step5.extraPhotosHint')}</p>
        <PhotoPicker value={extraPhotos} onChange={setExtraPhotos} />
      </div>
    </div>
  );
}
